// import { Flag } from '@mui/icons-material'
import { LocalizationProvider, LoadingButton } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {
  DialogTitle,
  DialogContent,
  Grid,
  Box,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  ListItemText,
  DialogActions,
} from "@mui/material";
import { DCButton, DCDialog } from "../../assets/theme/theme";
import {
  DCAutocomplete,
  DCAutocompleteTextField,
  DcInput,
} from "../../assets/theme/theme";
import { DCDatePicker } from "../Shared/DCDatePicker";
import { DCFormLabel } from "../Shared/DCFormLabel";
import constants from "../../constants";
import { useEditTravelHistoryModalNew } from "../../contexts/EditTravelHistoryModalContextNew";
import { dateFormat } from "../../utils/dateTimeUtils";

import { Flag } from "../Shared/Icons/Flag";
import { useCallback, useEffect } from "react";
import {
  airportList as worldAirportList,
  usaAirportList,
  useTravelDatesValidationNew,
} from "../../utils/travelHistoryUtils";
import { isEmpty } from "lodash";
import { usePreventRefreshDialogContext } from "../../contexts/PreventRefreshDialogContext";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface Props {
  travelState: "usa" | "world";
}

const EditTravelHistoryModalNew: React.FC<Props> = (props) => {
  const {
    open,
    closeEditTravelHistoryModal,
    selectedTravelHistory,
    setSelectedTravelHistory,
    updateTravelHistory,
    isSaveButtonDisabled,
    isUpdatingTravelHistory,
  } = useEditTravelHistoryModalNew();
  const { showPreventRefreshDialog, hidePreventRefreshDialog } = usePreventRefreshDialogContext();

  const {
    shouldDisableArrivalDate,
    shouldDisableDepartureDate,
    shouldDisableIsPresent,
  } = useTravelDatesValidationNew(selectedTravelHistory);

  const airportList =
    props.travelState === "usa" ? usaAirportList : worldAirportList;

  // handles the change of the arrival date
  const onChangeArrivalDate = useCallback(
    (_data: Date, val: number) => {
      const formattedDate = dateFormat(val); // format the date in YYYY-MM-DD format
      setSelectedTravelHistory((prev: any) => ({
        ...prev,
        arrival_date: formattedDate,
        arrival_date_parsed: _data,
      }));
    },
    [setSelectedTravelHistory]
  );

  // handles the change of the departure date
  const onChangeDepartureDate = useCallback(
    (_data: Date, val: number) => {
      const formattedDate = dateFormat(val); // format the date in YYYY-MM-DD format
      setSelectedTravelHistory((prev: any) => ({
        ...prev,
        departure_date: formattedDate,
        departure_date_parsed: _data,
      }));
    },
    [setSelectedTravelHistory]
  );

  // handles the change of the arrival place
  const onChangeArrivalPlace = useCallback(
    (val: any) => {
      setSelectedTravelHistory((prev: any) => ({
        ...prev,
        country: val?.country,
        arrival_loc_label: val?.label,
        arrival_loc: val?.airport_code,
      }));
    },
    [setSelectedTravelHistory]
  );

  // handles the change of the departure place
  const onChangeDeparturePlace = useCallback(
    (val: any) => {
      setSelectedTravelHistory((prev: any) => ({
        ...prev,
        departure_loc_label: val?.label,
        departure_loc: val?.airport_code,
      }));
    },
    [setSelectedTravelHistory]
  );

  // handles the change of the isPresent checkbox
  const onChangeIsPresent = useCallback(
    (e: any) => {
      setSelectedTravelHistory((prev: any) => ({
        ...prev,
        isPresent: e.target.checked,
        departure_loc_label: "",
        departure_loc: "",
        departure_date: "",
        departure_date_parsed: null,
      }));
    },
    [setSelectedTravelHistory]
  );

  const isWorldTravel = props.travelState === "world";

  useEffect(() => {
    if (open)
      showPreventRefreshDialog("All the changes made will be lost, are you sure want to continue?")
    else
      hidePreventRefreshDialog()
  }, [hidePreventRefreshDialog, showPreventRefreshDialog, open])

  return (
    <DCDialog
      open={open}
      // onClose={closeEditTravelHistoryModal}s
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "1000px",
          },
        },
      }}
    >
      <DialogTitle>
        Edit
        {isWorldTravel ? "" : " USA"} Travel log
      </DialogTitle>
      <DialogContent className="edit-travel-log">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={3} className="edit-travel-log-contain">
            <Grid item xs={6} className="edit-travel-log-input">
              <DCFormLabel htmlFor="departing-city-country">
                Port Of Entry
              </DCFormLabel>
              <DCAutocomplete
                id="departing-city-country"
                options={airportList}
                value={selectedTravelHistory?.arrival_loc_label}
                onChange={(_, val: any) => onChangeArrivalPlace(val)}
                renderInput={(params) => (
                  <DCAutocompleteTextField
                    {...params}
                    error={isEmpty(selectedTravelHistory?.arrival_loc)}
                    helperText={
                      isEmpty(selectedTravelHistory?.arrival_loc)
                        ? "Required"
                        : ""
                    }
                    required
                    placeholder="Select City, Country"
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} className="edit-travel-log-input">
              <DCFormLabel htmlFor="visiting-city-country">
                Port Of Exit
              </DCFormLabel>
              <DCAutocomplete
                id="visiting-city-country"
                key={
                  selectedTravelHistory?.isPresent
                    ? "departure_present"
                    : "visiting-city-country"
                }
                options={airportList}
                disabled={selectedTravelHistory?.isPresent}
                value={selectedTravelHistory?.departure_loc_label}
                onChange={(_, val: any) => onChangeDeparturePlace(val)}
                renderInput={(params) => (
                  <DCAutocompleteTextField
                    {...params}
                    error={
                      selectedTravelHistory?.isPresent
                        ? false
                        : isEmpty(selectedTravelHistory?.departure_loc)
                    }
                    helperText={
                      selectedTravelHistory?.isPresent
                        ? false
                        : isEmpty(selectedTravelHistory?.departure_loc)
                        ? "Required"
                        : ""
                    }
                    required
                    placeholder={
                      selectedTravelHistory?.isPresent
                        ? "Currently in the country"
                        : "Select City, Country"
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} className="edit-travel-log-input">
              <DCFormLabel htmlFor="arrival-date">
                Arrival Date
                {selectedTravelHistory?.country
                  ? ` (${selectedTravelHistory?.country})`
                  : ""}
                <Flag
                  code={selectedTravelHistory?.country || ""}
                  width={20}
                  style={{ marginLeft: 8, marginBottom: -1 }}
                />
              </DCFormLabel>
              <DCDatePicker
                id="arrival-date"
                value={selectedTravelHistory?.arrival_date_parsed as Date}
                onChange={onChangeArrivalDate}
                shouldDisableDate={shouldDisableArrivalDate}
              />
            </Grid>
            <Grid item xs={6} className="edit-travel-log-input">
              <Box position="relative">
                <DCFormLabel htmlFor="departure-date">
                  Departure Date
                  {selectedTravelHistory?.country
                    ? ` (${selectedTravelHistory?.country})`
                    : ""}
                  <Flag
                    code={selectedTravelHistory?.country || ""}
                    width={20}
                    style={{ marginLeft: 8, marginBottom: -1 }}
                  />
                </DCFormLabel>
                <DCDatePicker
                  id="departure-date"
                  value={selectedTravelHistory?.departure_date_parsed as Date}
                  disabled={
                    !selectedTravelHistory?.arrival_date_parsed ||
                    selectedTravelHistory.isPresent
                  }
                  placeholder={
                    selectedTravelHistory?.isPresent ? "In country" : undefined
                  }
                  onChange={onChangeDepartureDate}
                  shouldDisableDate={shouldDisableDepartureDate}
                />
                <Box position="absolute" bottom={-40}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={shouldDisableIsPresent()}
                        checked={selectedTravelHistory?.isPresent}
                        onChange={onChangeIsPresent}
                      />
                    }
                    label="Currently in the country"
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6} className="edit-travel-log-input">
              <DCFormLabel className="visa-type-input" htmlFor="visa-status">Visa Type</DCFormLabel>
              <Box>
                {isWorldTravel ? (
                  <DcInput
                    id="visa-status"
                    fullWidth
                    value={selectedTravelHistory?.visa_status}
                    onChange={(e) =>
                      setSelectedTravelHistory((prev: any) => ({
                        ...prev,
                        visa_status: e.target.value,
                      }))
                    }
                    placeholder="e.g. : H1B, F1, B2"
                    inputProps={{ style: { textTransform: "uppercase" } }}
                  />
                ) : (
                  <Select
                    fullWidth
                    value={selectedTravelHistory?.visa_status}
                    onChange={(e) =>
                      setSelectedTravelHistory((prev: any) => ({
                        ...prev,
                        visa_status: e.target.value,
                      }))
                    }
                    input={
                      <DcInput
                        id="visa-status"
                        placeholder="e.g. : H1B, F1, B2"
                        inputProps={{ style: { textTransform: "uppercase" } }}
                      />
                    }
                    MenuProps={MenuProps}
                  >
                    {constants.VisaStatusList.map((year) => (
                      <MenuItem key={year} value={year}>
                        <ListItemText primary={year} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <DCButton
          variant="outlined"
          aria-label="close window"
          onClick={closeEditTravelHistoryModal}
        >
          Close
        </DCButton>

        <LoadingButton
          loadingPosition="center"
          aria-label="save custom card"
          variant="contained"
          loading={isUpdatingTravelHistory}
          onClick={updateTravelHistory}
          disabled={isSaveButtonDisabled()}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </DCDialog>
  );
};

export default EditTravelHistoryModalNew;
